/**
 * Crew roster CRUD body — rendered inside the "MANAGE CREW" tab of
 * `MeetCrewModal`, only when power-user mode is unlocked. Lets the
 * facilitator add / remove / rename / change roles / reset passwords.
 *
 * Adapted from `Loveable v1/src/components/UserManagementModal.tsx`:
 *   - framer-motion → motion (rebranded fork; same API).
 *   - FontAwesomeIcon → lucide-react inline icons.
 *   - Dialog wrapper stripped — the parent `MeetCrewModal` owns the
 *     Dialog so this can sit inside a TabsContent.
 *   - UserProfile shape dropped the `background` field — we don't ship
 *     per-user backgrounds in this build (yet).
 */

import {
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, KeyRound, Search, Trash2, UserCheck } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { availableRoles, validatePassword, validateUsername } from "@/content/users";
import { userManagement as content } from "@/content/ui";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isMultiClientEnabled, isZitadelMode } from "@/env";
import { createClientUser, listClients, type ClientEntry } from "@/lib/docs-api";
import { listActiveSeedClients } from "@/lib/seed-clients";
import { roleKeyFromDisplay } from "@/features/auth/roles";
import { toast } from "sonner";

const MAX_USER_LIST_HEIGHT = "340px";
const DXC_AFFILIATION = "dxc";
const CLIENT_ADD_ROLES = availableRoles.filter((r) => r !== "MISSION FACILITATOR");

/** Console chrome — matches Meet Crew / Power User (DXC tokens, no glass). */
const chrome = {
  input: {
    background: "rgba(var(--accent-true-blue-rgb), 0.06)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.2)",
    color: "var(--text-primary)",
  } as const,
  panel: {
    background: "var(--bg-panel-alt)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
  } as const,
  row: {
    background: "rgba(var(--accent-true-blue-rgb), 0.04)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.1)",
  } as const,
  rowActive: {
    background: "rgba(var(--accent-true-blue-rgb), 0.08)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.22)",
  } as const,
  pill: {
    color: "var(--accent-true-blue)",
    border: "1px solid var(--accent-true-blue)",
    background: "rgba(var(--accent-true-blue-rgb), 0.12)",
  } as const,
  cta: {
    color: "var(--accent-sky)",
    border: "1px solid rgba(var(--accent-true-blue-rgb), 0.45)",
    background: "rgba(var(--accent-true-blue-rgb), 0.1)",
    boxShadow: "0 0 12px rgba(var(--accent-true-blue-rgb), 0.1)",
  } as const,
  goldPanel: {
    background: "rgba(var(--accent-gold-rgb, 201, 162, 39), 0.06)",
    border: "1px solid rgba(var(--accent-gold-rgb, 201, 162, 39), 0.35)",
  } as const,
  rule: "1px solid rgba(var(--accent-true-blue-rgb), 0.12)",
};

const inputClass =
  "font-mono text-xs outline-none tracking-widest rounded px-2.5 py-1.5";

/* ── Role dropdown (Radix DropdownMenu — Dialog-aware so clicks don't ───
   get eaten by Radix's `inert` layer on portaled-to-body content) ────── */

interface RoleDropdownProps {
  value: string;
  onChange: (role: string) => void;
  disabled?: boolean;
  compact?: boolean;
  roles?: string[];
}

function RoleDropdown({ value, onChange, disabled, compact, roles }: RoleDropdownProps) {
  const options = roles ?? availableRoles;
  return (
    <div className={`${compact ? "w-40" : "w-44"} flex-shrink-0`}>
      <DropdownMenu>
        <DropdownMenuTrigger
          asChild
          disabled={disabled}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className={`flex w-full items-center justify-between gap-1.5 rounded border px-2.5 py-1.5 font-mono tracking-widest outline-none transition-colors disabled:opacity-40 ${
              compact ? "text-fs-label" : "text-xs"
            }`}
            style={{
              ...chrome.input,
              borderColor: "rgba(var(--accent-true-blue-rgb), 0.2)",
            }}
          >
            <span className="truncate" style={{ color: "var(--text-primary)" }}>
              {value}
            </span>
            <ChevronDown
              className="h-3 w-3 transition-transform data-[state=open]:rotate-180"
              style={{ color: "var(--text-muted)" }}
              aria-hidden
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={4}
          className={`${compact ? "w-40" : "w-44"} overflow-hidden p-0`}
          style={{
            background: "var(--bg-panel)",
            border: "1px solid rgba(var(--accent-true-blue-rgb), 0.25)",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.35)",
          }}
        >
          {options.map((r) => (
            <DropdownMenuItem
              key={r}
              onSelect={() => onChange(r)}
              className={`w-full cursor-pointer rounded-none px-3 py-2 font-mono tracking-widest ${
                compact ? "text-fs-label" : "text-xs"
              }`}
              style={{
                color: r === value ? "var(--accent-true-blue)" : "var(--text-secondary)",
                background:
                  r === value ? "rgba(var(--accent-true-blue-rgb), 0.1)" : "transparent",
              }}
            >
              {r === value && (
                <span
                  className="mr-2 inline-block h-1.5 w-1.5 rounded-full align-middle"
                  style={{ background: "var(--accent-true-blue)" }}
                />
              )}
              {r}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/* ── Body ──────────────────────────────────────────────────────────────── */

export function UserManagementBody() {
  const {
    users,
    pendingUsers,
    user,
    addUser,
    approveUser,
    removeUser,
    updateUserRole,
    resetPassword,
    updateUserDetails,
    isManagedExternally,
  } = useAuth();

  const multiClient = isMultiClientEnabled();
  const zitadel = isZitadelMode();

  const [newUsername, setNewUsername] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState(availableRoles[1] ?? availableRoles[0] ?? "");
  const [affiliationKind, setAffiliationKind] = useState<"dxc" | "client">("dxc");
  const [affiliationClientId, setAffiliationClientId] = useState("");
  const [addError, setAddError] = useState("");

  const clientsQuery = useQuery({
    queryKey: ["clients"],
    queryFn: listClients,
    enabled: multiClient && zitadel,
  });
  const activeClients: ClientEntry[] = multiClient
    ? zitadel
      ? (clientsQuery.data ?? []).filter((c) => c.status === "active" && c.id !== "dxc")
      : listActiveSeedClients().filter((c) => c.id !== "local")
    : [];

  const clientNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of activeClients) map.set(c.id, c.name);
    return map;
  }, [activeClients]);

  const rolesForAdd =
    multiClient && affiliationKind === "client" ? CLIENT_ADD_ROLES : availableRoles;

  const [search, setSearch] = useState("");
  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.trim().toLowerCase();
    return users.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q) ||
        (u.affiliation ?? "").toLowerCase().includes(q) ||
        (clientNameById.get(u.affiliation ?? "") ?? "").toLowerCase().includes(q),
    );
  }, [users, search, clientNameById]);

  const filteredPending = useMemo(() => {
    if (!search.trim()) return pendingUsers;
    const q = search.trim().toLowerCase();
    return pendingUsers.filter(
      (u) =>
        u.displayName.toLowerCase().includes(q) ||
        u.username.toLowerCase().includes(q) ||
        u.role.toLowerCase().includes(q),
    );
  }, [pendingUsers, search]);

  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [resetInputs, setResetInputs] = useState<Record<string, string>>({});
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [editNames, setEditNames] = useState<Record<string, { displayName: string; username: string }>>({});
  const [editError, setEditError] = useState<string | null>(null);

  const validateUserInput = (username: string, password: string, displayName: string): string | null => {
    if (!username || !password || !displayName) return "All fields are required.";
    return validateUsername(username) ?? validatePassword(password);
  };

  const handleAdd = async () => {
    const u = newUsername.trim().toLowerCase();
    const p = newPassword.trim();
    const d = newDisplayName.trim();
    const err = validateUserInput(u, p, d);
    if (err) {
      setAddError(err);
      return;
    }
    if (multiClient && affiliationKind === "client" && !affiliationClientId) {
      setAddError("Pick a client for this user.");
      return;
    }

    if (multiClient && affiliationKind === "client" && zitadel) {
      const roleKey = roleKeyFromDisplay(newRole);
      if (!roleKey || roleKey === "MISSION_FACILITATOR") {
        setAddError("Client users cannot be facilitators.");
        return;
      }
      try {
        const cred = await createClientUser(affiliationClientId, {
          username: u,
          role: roleKey,
          password: p,
        });
        toast.success(
          cred.status === "created"
            ? `Created ${cred.email}`
            : `User already existed: ${cred.email}`,
        );
        setNewUsername("");
        setNewDisplayName("");
        setNewPassword("");
        setNewRole(CLIENT_ADD_ROLES[0] ?? "");
        setAddError("");
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(`Add client user failed: ${msg}`);
        setAddError("Add failed — see toast.");
      }
      return;
    }

    const affiliation =
      multiClient && affiliationKind === "client"
        ? affiliationClientId
        : multiClient
          ? DXC_AFFILIATION
          : undefined;
    const ok = await addUser({
      username: u,
      password: p,
      displayName: d,
      role: newRole,
      ...(affiliation ? { affiliation } : {}),
    });
    if (ok) {
      setNewUsername("");
      setNewDisplayName("");
      setNewPassword("");
      setNewRole(availableRoles[1] ?? availableRoles[0] ?? "");
      setAddError("");
    } else {
      setAddError(isManagedExternally ? "Add failed — see toast." : content.duplicateError);
    }
  };

  const handleRemove = async (username: string) => {
    if (await removeUser(username)) {
      setConfirmRemove(null);
      setExpandedUser(null);
    }
  };

  const handleResetPassword = async (username: string) => {
    const pw = resetInputs[username]?.trim();
    if (!pw) return;
    const pwError = validatePassword(pw);
    if (pwError) {
      setEditError(pwError);
      return;
    }
    if (await resetPassword(username, pw)) {
      setResetInputs((prev) => ({ ...prev, [username]: "" }));
      setEditError(null);
    } else {
      setEditError(isManagedExternally ? "See toast for details." : "Failed to reset password.");
    }
  };

  const startEditing = (u: { username: string; displayName: string }) => {
    setEditNames((prev) => ({ ...prev, [u.username]: { displayName: u.displayName, username: u.username } }));
    setEditError(null);
  };

  const handleSaveDetails = async (originalUsername: string) => {
    const edits = editNames[originalUsername];
    if (!edits) return;
    const trimmedName = edits.displayName.trim();
    const trimmedUser = edits.username.trim().toLowerCase();
    if (!trimmedName || trimmedName.length < 2) {
      setEditError("Display name must be at least 2 characters.");
      return;
    }
    if (!trimmedUser || validateUsername(trimmedUser) !== null) {
      setEditError("Username must be 3+ chars, alphanumeric/dash/underscore only.");
      return;
    }
    const ok = await updateUserDetails(originalUsername, {
      displayName: trimmedName,
      username: trimmedUser !== originalUsername ? trimmedUser : undefined,
    });
    if (ok) {
      setEditNames((prev) => {
        const next = { ...prev };
        delete next[originalUsername];
        return next;
      });
      setEditError(null);
      if (trimmedUser !== originalUsername) setExpandedUser(trimmedUser);
    } else {
      setEditError(
        isManagedExternally ? "See toast for details." : "Username already taken.",
      );
    }
  };

  const stop = (e: ReactMouseEvent) => e.stopPropagation();

  const affiliationSelectValue =
    affiliationKind === "client" ? affiliationClientId : DXC_AFFILIATION;

  const onAffiliationSelect = (value: string) => {
    if (value === DXC_AFFILIATION) {
      setAffiliationKind("dxc");
      setAffiliationClientId("");
      if (!availableRoles.includes(newRole)) {
        setNewRole(availableRoles[1] ?? availableRoles[0] ?? "");
      }
    } else {
      setAffiliationKind("client");
      setAffiliationClientId(value);
      if (!CLIENT_ADD_ROLES.includes(newRole)) {
        setNewRole(CLIENT_ADD_ROLES[0] ?? "");
      }
    }
  };

  return (
    <div className="flex max-h-[70vh] flex-col gap-4">
      <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
        {multiClient && (
          <select
            value={affiliationSelectValue}
            onChange={(e) => onAffiliationSelect(e.target.value)}
            className={`${inputClass} min-w-[8rem]`}
            style={chrome.input}
            aria-label={content.affiliationLabel}
          >
            <option value={DXC_AFFILIATION}>{content.affiliationDxc}</option>
            {activeClients.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
        <input
          type="text"
          value={newDisplayName}
          onChange={(e) => setNewDisplayName(e.target.value)}
          placeholder={content.nameLabel}
          className={`${inputClass} w-32 min-w-[100px] flex-1`}
          style={chrome.input}
        />
        <input
          type="text"
          value={newUsername}
          onChange={(e) => {
            setNewUsername(e.target.value);
            setAddError("");
          }}
          placeholder={content.usernameLabel}
          className={`${inputClass} w-28 min-w-[90px] flex-1`}
          style={chrome.input}
        />
        <input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && void handleAdd()}
          placeholder={content.passwordLabel}
          className={`${inputClass} w-28 min-w-[90px] flex-1`}
          style={chrome.input}
        />
        <RoleDropdown
          value={newRole}
          onChange={setNewRole}
          compact
          roles={rolesForAdd}
        />
        <motion.button
          whileTap={{ scale: 0.97 }}
          onClick={() => void handleAdd()}
          disabled={
            !newUsername.trim() ||
            !newPassword.trim() ||
            !newDisplayName.trim() ||
            (multiClient && affiliationKind === "client" && !affiliationClientId)
          }
          className="rounded-md px-3 py-1.5 font-mono text-fs-label font-bold tracking-[0.15em] whitespace-nowrap uppercase transition-colors disabled:opacity-30"
          style={chrome.cta}
        >
          {content.addButton}
        </motion.button>
      </div>
      {addError && (
        <p
          className="-mt-2 font-mono text-fs-label tracking-widest"
          style={{ color: "var(--accent-red)" }}
        >
          {addError}
        </p>
      )}

      {isManagedExternally && pendingUsers.length > 0 && (
        <div className="flex flex-shrink-0 flex-col gap-1">
          {filteredPending.map((u) => (
            <div key={u.username} className="flex items-center gap-3 px-1 py-1.5">
              <div className="min-w-0 flex-1">
                <p
                  className="truncate font-mono text-fs-label tracking-widest"
                  style={{ color: "var(--text-primary)" }}
                >
                  {u.displayName}
                </p>
                <p
                  className="truncate font-mono text-fs-micro tracking-widest"
                  style={{ color: "var(--text-muted)" }}
                >
                  @{u.username}
                </p>
              </div>
              <RoleDropdown
                value={u.role}
                onChange={(r) => void updateUserRole(u.username, r)}
                compact
              />
              <motion.button
                whileTap={{ scale: 0.97 }}
                onClick={() => void approveUser(u.username)}
                className="flex items-center gap-1 rounded-md border px-2.5 py-1 font-mono text-fs-micro tracking-widest whitespace-nowrap uppercase"
                style={chrome.cta}
              >
                <UserCheck className="h-3 w-3" aria-hidden />
                {content.approveButton}
              </motion.button>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-shrink-0 items-center gap-2">
        <Search
          className="h-3 w-3"
          style={{ color: "var(--text-muted)", opacity: 0.5 }}
          aria-hidden
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search"
          className="flex-1 bg-transparent font-mono text-fs-label tracking-widest outline-none"
          style={{ color: "var(--text-primary)" }}
        />
        <span
          className="font-mono text-fs-micro"
          style={{ color: "var(--text-muted)" }}
        >
          {filteredUsers.length}/{users.length}
        </span>
      </div>

      <div
        className="flex min-h-0 flex-col gap-0.5 overflow-y-auto pr-1"
        style={{ maxHeight: MAX_USER_LIST_HEIGHT }}
      >
        {filteredUsers.map((u) => {
          const isSelf = u.username === user?.username;
          const isExpanded = expandedUser === u.username;
          const affiliationText =
            (u.affiliation ?? DXC_AFFILIATION) === DXC_AFFILIATION
              ? content.affiliationDxc
              : (clientNameById.get(u.affiliation ?? "") ?? u.affiliation);
          return (
            <div key={u.username}>
              <div
                className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 transition-colors"
                style={
                  isExpanded
                    ? {
                        background: "rgba(var(--accent-true-blue-rgb), 0.08)",
                        border: "1px solid rgba(var(--accent-true-blue-rgb), 0.18)",
                      }
                    : undefined
                }
                onClick={() => setExpandedUser(isExpanded ? null : u.username)}
              >
                <div className="min-w-0 flex-1">
                  <p
                    className="truncate font-mono text-fs-label leading-tight tracking-widest"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {u.displayName}
                    {isSelf && (
                      <span
                        className="ml-1.5 text-fs-micro"
                        style={{ color: "var(--accent-true-blue)", opacity: 0.65 }}
                      >
                        (you)
                      </span>
                    )}
                  </p>
                  <p
                    className="truncate font-mono text-fs-micro leading-tight tracking-widest"
                    style={{ color: "var(--text-muted)" }}
                  >
                    @{u.username}
                    {multiClient && affiliationText && (
                      <span className="ml-1.5">· {affiliationText}</span>
                    )}
                  </p>
                </div>

                <RoleDropdown
                  value={u.role}
                  onChange={(r) => void updateUserRole(u.username, r)}
                  disabled={isSelf}
                  compact
                />

                <div className="flex w-14 flex-shrink-0 items-center justify-center gap-1.5">
                  {!isSelf ? (
                    confirmRemove === u.username ? (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleRemove(u.username);
                          }}
                          className="rounded-sm border px-1.5 py-0.5 font-mono text-fs-micro tracking-widest transition-colors"
                          style={{
                            color: "var(--accent-red)",
                            borderColor: "rgba(var(--accent-red-rgb, 220, 53, 69), 0.45)",
                            background: "rgba(var(--accent-red-rgb, 220, 53, 69), 0.08)",
                          }}
                        >
                          YES
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setConfirmRemove(null);
                          }}
                          className="rounded-sm border px-1.5 py-0.5 font-mono text-fs-micro tracking-widest transition-colors"
                          style={{
                            color: "var(--text-muted)",
                            borderColor: "rgba(var(--accent-true-blue-rgb), 0.2)",
                          }}
                        >
                          NO
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmRemove(u.username);
                        }}
                        className="p-1 opacity-0 transition-colors group-hover:opacity-100"
                        style={{ color: "var(--text-muted)" }}
                        aria-label="Remove"
                      >
                        <Trash2 className="h-3 w-3" aria-hidden />
                      </button>
                    )
                  ) : (
                    <span className="text-fs-micro" style={{ color: "var(--text-muted)", opacity: 0.35 }}>
                      —
                    </span>
                  )}
                </div>
              </div>

              <AnimatePresence>
                {isExpanded && !isSelf && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="ml-2 flex flex-col gap-2 px-2 py-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editNames[u.username]?.displayName ?? u.displayName}
                          onFocus={() => {
                            if (!editNames[u.username]) startEditing(u);
                          }}
                          onChange={(e) =>
                            setEditNames((prev) => ({
                              ...prev,
                              [u.username]: {
                                ...(prev[u.username] ?? {
                                  displayName: u.displayName,
                                  username: u.username,
                                }),
                                displayName: e.target.value,
                              },
                            }))
                          }
                          onClick={stop}
                          placeholder={content.nameLabel}
                          className={`${inputClass} flex-1 text-fs-label`}
                          style={chrome.input}
                        />
                        <input
                          type="text"
                          value={editNames[u.username]?.username ?? u.username}
                          onFocus={() => {
                            if (!editNames[u.username]) startEditing(u);
                          }}
                          onChange={(e) =>
                            setEditNames((prev) => ({
                              ...prev,
                              [u.username]: {
                                ...(prev[u.username] ?? {
                                  displayName: u.displayName,
                                  username: u.username,
                                }),
                                username: e.target.value,
                              },
                            }))
                          }
                          onKeyDown={(e) => e.key === "Enter" && void handleSaveDetails(u.username)}
                          onClick={stop}
                          placeholder={content.usernameLabel}
                          className={`${inputClass} flex-1 text-fs-label`}
                          style={chrome.input}
                        />
                        {editNames[u.username] && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              void handleSaveDetails(u.username);
                            }}
                            className="rounded-sm border px-2 py-1 font-mono text-fs-micro tracking-widest whitespace-nowrap transition-colors"
                            style={{
                              color: "var(--accent-true-blue)",
                              borderColor: "rgba(var(--accent-true-blue-rgb), 0.35)",
                              background: "rgba(var(--accent-true-blue-rgb), 0.08)",
                            }}
                          >
                            SAVE
                          </button>
                        )}
                      </div>
                      {editError && expandedUser === u.username && (
                        <p
                          className="font-mono text-fs-micro tracking-widest"
                          style={{ color: "var(--accent-red)" }}
                        >
                          {editError}
                        </p>
                      )}

                      <div className="flex items-center gap-2">
                        <KeyRound
                          className="h-3 w-3"
                          style={{ color: "var(--text-muted)", opacity: 0.6 }}
                          aria-hidden
                        />
                        <input
                          type="password"
                          value={resetInputs[u.username] ?? ""}
                          onChange={(e) =>
                            setResetInputs((prev) => ({ ...prev, [u.username]: e.target.value }))
                          }
                          onKeyDown={(e) => e.key === "Enter" && void handleResetPassword(u.username)}
                          onClick={stop}
                          placeholder={content.newPasswordHint}
                          className={`${inputClass} flex-1 text-fs-label`}
                          style={chrome.input}
                        />
                        <button
                          onClick={() => void handleResetPassword(u.username)}
                          disabled={!resetInputs[u.username]?.trim()}
                          className="rounded-sm border px-2 py-1 font-mono text-fs-micro tracking-widest whitespace-nowrap transition-colors disabled:opacity-30"
                          style={{
                            color: "var(--accent-true-blue)",
                            borderColor: "rgba(var(--accent-true-blue-rgb), 0.35)",
                            background: "rgba(var(--accent-true-blue-rgb), 0.08)",
                          }}
                        >
                          {content.resetPasswordLabel}
                        </button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}
